import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Brain, Sparkles, Settings as SettingsIcon, Eye, ShieldAlert, Bell,
  Volume2, MessageSquare, Loader2, Trash2, RefreshCw, Bug,
  Heart, Target, Zap, X as XIcon, Check, AlertCircle, Activity, Shield,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { EntityShell } from "../components/shell";
import DictTextarea from "../components/DictTextarea";

/**
 * /settings/hidro — per-user Hidro control panel.
 *
 * Three sections:
 *   1. Tone & cadence knobs (§14 + §19 + §20)
 *   2. "What Hidro knows about me" (§15)
 *   3. Bug reporting + recent feedback (§20 + §16)
 */
const TONE_OPTS = [
  { v: "match_user", label: "Match my tone (default)" },
  { v: "warm", label: "Warm" },
  { v: "terse", label: "Terse — just the facts" },
  { v: "formal", label: "Formal" },
  { v: "playful", label: "Playful" },
];
const LENGTH_OPTS = [
  { v: "one_line", label: "One-liner where possible" },
  { v: "short", label: "Short (default)" },
  { v: "long", label: "Long & detailed" },
];
const NUDGE_OPTS = [
  { v: "low", label: "Low — don't nudge me unless urgent" },
  { v: "medium", label: "Medium (default)" },
  { v: "high", label: "High — push me, I want it" },
];
const ACTION_OPTS = [
  { v: "", label: "Use my role default" },
  { v: "silent", label: "Silent — just do it (logged + undoable)" },
  { v: "notify_target", label: "Tell affected users" },
  { v: "confirm_first", label: "Always confirm before acting" },
];
const SELFDIAG_OPTS = [
  { v: "off", label: "Off" },
  { v: "daily", label: "Daily (default)" },
  { v: "weekly", label: "Weekly" },
  { v: "monthly", label: "Monthly" },
];

export default function HidroSettings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "owner" || user?.role === "admin";
  const [settings, setSettings] = useState(null);
  const [knows, setKnows] = useState(null);
  const [recentFeedback, setRecentFeedback] = useState([]);
  const [recentDiagnoses, setRecentDiagnoses] = useState([]);
  const [diagnosing, setDiagnosing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [err, setErr] = useState("");
  const [bugOpen, setBugOpen] = useState(false);
  const [bugReason, setBugReason] = useState("");
  const [bugSent, setBugSent] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, k, fb, sd] = await Promise.all([
        api.get("/hidro/settings"),
        api.get("/hidro/what-you-know"),
        api.get("/hidro/ai-feedback/my?limit=20").catch(() => ({ data: { items: [] } })),
        api.get("/hidro/self-diagnose/recent?limit=5").catch(() => ({ data: { items: [] } })),
      ]);
      setSettings(s.data);
      setKnows(k.data);
      setRecentFeedback(fb.data?.items || []);
      setRecentDiagnoses(sd.data?.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load Hidro settings");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const runSelfDiagnose = async () => {
    setDiagnosing(true);
    try {
      await api.post("/hidro/self-diagnose", {});
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not run self-diagnosis");
    }
    setDiagnosing(false);
  };

  const patch = async (key, value) => {
    setSaving(true);
    setErr("");
    try {
      const body = key === "quiet_hours" ? { quiet_hours: value } : { [key]: value };
      const { data } = await api.patch("/hidro/settings", body);
      setSettings(data);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save");
    }
    setSaving(false);
  };

  const [pendingPurge, setPendingPurge] = useState(null);
  const purge = (pattern) => setPendingPurge(pattern);
  const confirmPurge = async () => {
    if (!pendingPurge) return;
    try {
      await api.post("/hidro/what-you-know/purge", { pattern: pendingPurge });
      setPendingPurge(null);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not purge");
    }
  };

  const [pendingWipe, setPendingWipe] = useState(false);
  const wipe = () => setPendingWipe(true);
  const confirmWipe = async () => {
    try {
      await api.post("/hidro/what-you-know/wipe", { keep_explicit: true });
      setPendingWipe(false);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not wipe");
    }
  };

  const reportBug = async () => {
    if (!bugReason.trim()) return;
    try {
      await api.post("/hidro/bugs", {
        kind: "user_report",
        severity: "medium",
        surface: "settings_hidro",
        route: "/settings/hidro",
        user_reason: bugReason.trim(),
      });
      setBugSent(true);
      setBugReason("");
      setTimeout(() => { setBugOpen(false); setBugSent(false); }, 2000);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not file bug");
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-xs text-[#647087]">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading Hidro settings…
      </div>
    );
  }

  return (
    <EntityShell
      kindLabel="Account · Hidro"
      title="Hidro settings"
      backTo=".."
      actions={savedFlash ? (
        <span className="text-[10px] text-emerald-300 inline-flex items-center gap-1" data-testid="hidro-settings-saved-pill">
          <Check className="w-3 h-3" /> Saved
        </span>
      ) : null}
    >
    <div className="space-y-5 max-w-5xl" data-testid="hidro-settings-page">
      <p className="text-sm text-[#a0aabf]">
        How Hidro talks to you, what it remembers, and how it acts on your behalf.
        Every setting takes effect on the next AI call — no redeploy, no waiting.
      </p>

      {err && (
        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-3 py-2" data-testid="hidro-settings-err">
          {err}
        </div>
      )}

      {/* Inline 2-step purge / wipe confirmations (Trust Contract Rule 2) */}
      {pendingPurge && (
        <div className="hv-card border-rose-500/40 bg-rose-500/5 p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="hidro-purge-confirm">
          <span className="text-xs text-rose-200">
            Tell Hidro to ignore <strong>"{pendingPurge}"</strong> going forward?
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPendingPurge(null)} className="hv-btn hv-btn-ghost text-xs" data-testid="hidro-purge-cancel">Cancel</button>
            <button onClick={confirmPurge} className="hv-btn hv-btn-primary text-xs bg-rose-500 hover:bg-rose-400" data-testid="hidro-purge-confirm-btn">Confirm purge</button>
          </div>
        </div>
      )}
      {pendingWipe && (
        <div className="hv-card border-rose-500/40 bg-rose-500/5 p-3 flex items-center justify-between gap-2 flex-wrap" data-testid="hidro-wipe-confirm">
          <span className="text-xs text-rose-200">
            Wipe inferred patterns? Your explicit preferences are kept. This cannot be undone.
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPendingWipe(false)} className="hv-btn hv-btn-ghost text-xs" data-testid="hidro-wipe-cancel">Cancel</button>
            <button onClick={confirmWipe} className="hv-btn hv-btn-primary text-xs bg-rose-500 hover:bg-rose-400" data-testid="hidro-wipe-confirm-btn">Confirm wipe</button>
          </div>
        </div>
      )}

      {/* ── Tone & cadence knobs ─────────────────────────────────────── */}
      <section className="hv-card p-5 space-y-4" data-testid="hidro-knobs">
        <div className="flex items-center gap-2">
          <SettingsIcon className="w-4 h-4 text-[#1FB6C1]" />
          <h2 className="font-display text-lg font-bold">How Hidro talks to you</h2>
        </div>
        <Knob
          label="Tone"
          icon={<MessageSquare className="w-3 h-3" />}
          value={settings.tone}
          options={TONE_OPTS}
          onChange={(v) => patch("tone", v)}
          testId="hidro-tone"
        />
        <Knob
          label="Length preference"
          icon={<Volume2 className="w-3 h-3" />}
          value={settings.length_preference}
          options={LENGTH_OPTS}
          onChange={(v) => patch("length_preference", v)}
          testId="hidro-length"
        />
        <Knob
          label="Nudge sensitivity"
          icon={<Bell className="w-3 h-3" />}
          value={settings.nudge_sensitivity}
          options={NUDGE_OPTS}
          onChange={(v) => patch("nudge_sensitivity", v)}
          testId="hidro-nudge"
        />
        <Knob
          label="When Hidro takes an action on your behalf"
          help={`Effective right now: ${settings.action_style_effective}`}
          icon={<Zap className="w-3 h-3" />}
          value={settings.action_style || ""}
          options={ACTION_OPTS}
          onChange={(v) => patch("action_style", v || null)}
          testId="hidro-action"
        />
        <Knob
          label="Self-diagnosis cadence"
          help="How often Hidro reviews its own performance and posts what it learned."
          icon={<RefreshCw className="w-3 h-3" />}
          value={settings.self_diagnosis_cadence}
          options={SELFDIAG_OPTS}
          onChange={(v) => patch("self_diagnosis_cadence", v)}
          testId="hidro-selfdiag"
        />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold mb-1 inline-flex items-center gap-1">
            <ShieldAlert className="w-3 h-3" /> Quiet hours (no nudges)
          </div>
          <div className="flex items-center gap-2">
            <NumInput
              label="From"
              value={settings.quiet_hours?.start ?? 21}
              onChange={(start) => patch("quiet_hours", { ...settings.quiet_hours, start })}
              testId="hidro-quiet-start"
            />
            <NumInput
              label="To"
              value={settings.quiet_hours?.end ?? 7}
              onChange={(end) => patch("quiet_hours", { ...settings.quiet_hours, end })}
              testId="hidro-quiet-end"
            />
            <span className="text-[10px] text-[#647087]">(24h, your local TZ)</span>
          </div>
        </div>
      </section>

      {/* ── Wave 25.11 — Teach Hidro per surface (plain-text rules) ───── */}
      <SurfacePrefsSection
        prefs={settings.surface_prefs || {}}
        onSave={(next) => patch("surface_prefs", next)}
      />

      {/* ── Admin shortcuts ─────────────────────────────────────────── */}
      {isAdmin && (
        <section className="hv-card p-4 flex flex-wrap items-center gap-2" data-testid="hidro-admin-links">
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold mr-2">Admin shortcuts</span>
          <Link to="/settings/hidro/health" className="hv-btn hv-btn-ghost text-xs" data-testid="hidro-link-health">
            <Activity className="w-3 h-3" /> Hidro health dashboard
          </Link>
          <Link to="/settings/roles" className="hv-btn hv-btn-ghost text-xs" data-testid="hidro-link-roles">
            <Shield className="w-3 h-3" /> Roles & permissions
          </Link>
        </section>
      )}

      {/* ── Self-diagnosis (Hidro learning about itself) ────────────── */}
      <section className="hv-card p-5 space-y-3" data-testid="hidro-selfdiag-section">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-[#1FB6C1]" />
          <h2 className="font-display text-lg font-bold">Hidro's self-diagnosis</h2>
          <button
            type="button"
            onClick={runSelfDiagnose}
            disabled={diagnosing}
            className="ml-auto hv-btn hv-btn-ghost text-xs"
            data-testid="hidro-selfdiag-run"
            title="Hidro reviews your last 24h of signals and posts what it learned. Auto-runs daily by default."
          >
            {diagnosing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Run now
          </button>
        </div>
        {recentDiagnoses.length === 0 ? (
          <div className="text-[11px] text-[#647087] italic">
            Nothing yet. Hidro auto-runs this {settings?.self_diagnosis_cadence || "daily"} — change cadence above.
            Once you've given a few 👍/👎 or rewritten an output, Hidro will start posting what it noticed.
          </div>
        ) : (
          <ul className="space-y-2">
            {recentDiagnoses.map((d) => (
              <li key={d.id} className="border border-[#1FB6C1]/20 rounded-md p-3 bg-[#1FB6C1]/[0.03]">
                <div className="text-[11px] text-[#647087] font-mono mb-1">
                  {(d.created_at || "").slice(0, 16).replace("T", " ")}
                </div>
                <div className="text-sm text-[#dde3ec] leading-relaxed">{d.summary}</div>
                {d.evidence && (
                  <div className="text-[10px] text-[#647087] mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
                    {Object.entries(d.evidence).filter(([_k, v]) => v && (Array.isArray(v) ? v.length : true)).map(([k, v]) => (
                      <span key={k} className="font-mono">
                        {k}: <span className="text-[#a0aabf]">{Array.isArray(v) ? v.join(", ") : String(v)}</span>
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── What Hidro knows about me ──────────────────────────────── */}
      <section className="hv-card p-5 space-y-3" data-testid="hidro-knows-section">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-[#1FB6C1]" />
          <h2 className="font-display text-lg font-bold">What Hidro knows about me</h2>
          <button
            type="button"
            onClick={wipe}
            className="ml-auto hv-btn hv-btn-ghost text-[11px] text-rose-300 hover:bg-rose-500/10"
            data-testid="hidro-wipe-inferred"
          >
            <Trash2 className="w-3 h-3" /> Wipe inferred patterns
          </button>
        </div>
        <div className="text-[11px] text-[#647087]">
          Tracking for {knows?.days_tracked || 0} days · {knows?.streak_days || 0}🔥 streak ·
          {" "}{knows?.signals_last_14d?.feedback_events || 0} feedback signals · {knows?.signals_last_14d?.check_ins || 0} check-ins (last 14d)
        </div>

        <KnowsRow
          icon={<Heart className="w-3 h-3 text-rose-300" />}
          label="Explicit preferences (you told Hidro this)"
          items={knows?.explicit_preferences || []}
          onPurge={purge}
          purgeable={false}
        />
        <KnowsRow
          icon={<Target className="w-3 h-3 text-emerald-300" />}
          label="Wins pattern (inferred)"
          items={normaliseList(knows?.inferred_patterns?.wins_pattern)}
          onPurge={purge}
        />
        <KnowsRow
          icon={<AlertCircle className="w-3 h-3 text-amber-300" />}
          label="Loss pattern (inferred)"
          items={normaliseList(knows?.inferred_patterns?.loss_pattern)}
          onPurge={purge}
        />
        <KnowsRow
          icon={<XIcon className="w-3 h-3 text-rose-300" />}
          label="Avoid patterns (from your 👎 feedback)"
          items={knows?.inferred_patterns?.avoid_patterns || []}
          onPurge={purge}
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] pt-1">
          <Stat label="Tone signal" value={knows?.inferred_patterns?.tone_signal} />
          <Stat label="Mood" value={knows?.inferred_patterns?.mood} />
          <Stat label="Fatigue" value={knows?.inferred_patterns?.fatigue_signal} />
          <Stat label="Velocity" value={knows?.inferred_patterns?.velocity} />
        </div>
      </section>

      {/* ── Recent feedback ─────────────────────────────────────────── */}
      <section className="hv-card p-5 space-y-3" data-testid="hidro-feedback-section">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#1FB6C1]" />
          <h2 className="font-display text-lg font-bold">Recent feedback you gave Hidro</h2>
        </div>
        {recentFeedback.length === 0 ? (
          <div className="text-[11px] text-[#647087] italic">
            No feedback yet — every time you 👍 / 👎 a Hidro output, it lands here and shapes future prompts.
          </div>
        ) : (
          <ul className="space-y-1.5 max-h-[260px] overflow-auto">
            {recentFeedback.map((f) => (
              <li key={f.id} className="text-[11px] flex items-start gap-2 border border-white/5 rounded px-2 py-1.5 bg-black/10">
                <span className={`shrink-0 ${f.thumbs === "up" ? "text-emerald-300" : "text-rose-300"}`}>
                  {f.thumbs === "up" ? "👍" : "👎"}
                </span>
                <span className="font-mono text-[#647087] shrink-0">{f.surface}</span>
                <span className="text-[#a0aabf] flex-1 truncate">{f.reason || "(no reason given)"}</span>
                <span className="text-[#475063] shrink-0 text-[10px]">{(f.created_at || "").slice(5, 16)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Report a Hidro bug ──────────────────────────────────────── */}
      <section className="hv-card p-5 space-y-3" data-testid="hidro-bug-section">
        <div className="flex items-center gap-2">
          <Bug className="w-4 h-4 text-rose-300" />
          <h2 className="font-display text-lg font-bold">Spotted a Hidro bug?</h2>
        </div>
        <p className="text-[11px] text-[#647087]">
          Something Hidro got wrong, broke, or hallucinated? Tell us — owners get a triage dashboard with everything reported here.
        </p>
        {!bugOpen ? (
          <button
            type="button"
            onClick={() => setBugOpen(true)}
            className="hv-btn hv-btn-ghost text-xs text-rose-300 hover:bg-rose-500/10"
            data-testid="hidro-bug-open"
          >
            <Bug className="w-3.5 h-3.5" /> Report a bug
          </button>
        ) : bugSent ? (
          <div className="text-emerald-300 text-xs inline-flex items-center gap-1">
            <Check className="w-3 h-3" /> Filed. Owners will see it on the dashboard.
          </div>
        ) : (
          <div className="space-y-2">
            <DictTextarea
              value={bugReason}
              onChange={(e) => setBugReason(e.target.value)}
              rows={4}
              placeholder="What did Hidro do wrong? Where? What did you expect instead?"
              className="hv-input text-sm w-full"
              data-testid="hidro-bug-reason"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setBugOpen(false); setBugReason(""); }}
                className="hv-btn hv-btn-ghost text-xs"
                data-testid="hidro-bug-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={reportBug}
                disabled={!bugReason.trim()}
                className="hv-btn hv-btn-primary text-xs"
                data-testid="hidro-bug-submit"
              >
                File it
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
    </EntityShell>
  );
}

function normaliseList(v) {
  if (!v) return [];
  if (Array.isArray(v)) return v.map((x) => typeof x === "string" ? x : (x?.text || JSON.stringify(x))).filter(Boolean);
  return [String(v)];
}

function Knob({ label, value, options, onChange, help, icon, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold inline-flex items-center gap-1">
        {icon} {label}
      </div>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="hv-input mt-1 text-sm"
        data-testid={`${testId}-select`}
      >
        {options.map((o) => (<option key={o.v} value={o.v}>{o.label}</option>))}
      </select>
      {help && <div className="text-[10px] text-[#647087] mt-1">{help}</div>}
    </div>
  );
}

function NumInput({ label, value, onChange, testId }) {
  return (
    <label className="inline-flex items-center gap-1 text-[11px] text-[#a0aabf]">
      <span>{label}</span>
      <input
        type="number"
        min={0}
        max={23}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="hv-input w-16 text-sm"
        data-testid={testId}
      />
    </label>
  );
}

function KnowsRow({ icon, label, items, onPurge, purgeable = true }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] font-bold inline-flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {items.map((p, i) => (
          <span
            key={`${p}-${i}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.03] text-[11px] text-[#dde3ec]"
          >
            {p}
            {purgeable && (
              <button
                type="button"
                onClick={() => onPurge(p)}
                title="Tell Hidro this isn't true / stop using it"
                className="text-[#647087] hover:text-rose-300"
                data-testid={`hidro-purge-${i}`}
              >
                <XIcon className="w-2.5 h-2.5" />
              </button>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/5 rounded px-2 py-1.5 bg-black/10">
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#647087]">{label}</div>
      <div className="text-[#dde3ec] truncate">{value || "—"}</div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────────────────────
 * SurfacePrefsSection (Wave 25.11) — "Teach Hidro" per-surface rules.
 * Each textarea persists to user_hidro_settings.surface_prefs[<key>] and
 * is appended verbatim to the system prompt of that surface, so the user
 * shapes Hidro's output for daily briefs, coaching, lead summaries, etc.
 * without any code redeploy.
 * ──────────────────────────────────────────────────────────────────── */
const SURFACE_DEFS = [
  {
    key: "daily_brief",
    label: "Daily brief",
    icon: "🌅",
    placeholder:
      "e.g. Lead with the 3 hottest leads (>R50k, expected close < 30d). Then yesterday's slips. No filler. Max 6 bullets.",
    hint: "Used when you ask 'run my daily brief' or 'what's on today'.",
  },
  {
    key: "coaching",
    label: "Personal coaching",
    icon: "🧠",
    placeholder:
      "e.g. Be blunt about losses. Don't sugar-coat. Always end with one specific action I can take today.",
    hint: "Shapes Coach check-in replies and any 'coach me' chat asks.",
  },
  {
    key: "lead_summary",
    label: "Lead summary",
    icon: "🎯",
    placeholder:
      "e.g. Open with: phase · last contact · expected close. Then 3 bullets: what's blocking, what we owe them, my next step.",
    hint: "Used when you ask Hidro to summarise a lead.",
  },
  {
    key: "customer_recap",
    label: "Customer recap",
    icon: "🏢",
    placeholder:
      "e.g. Order: spend last 12mo, open jobcards, oldest open ticket, last meeting outcome. No marketing fluff.",
    hint: "Used when you ask 'recap this customer' from a customer page.",
  },
  {
    key: "email_draft",
    label: "Email drafts",
    icon: "✉️",
    placeholder:
      "e.g. Short subject, no exclamation marks. Sign off 'Regards, Dirk'. Always include a clear next step. South African English.",
    hint: "Used when Hidro drafts emails for you.",
  },
  {
    key: "meeting_debrief",
    label: "Meeting debrief",
    icon: "🤝",
    placeholder:
      "e.g. Pull out: outcome, next step, owner, due date. Capture stakeholder mood (hot/warm/cold).",
    hint: "Used by the meeting voice-debrief refine flow.",
  },
  {
    key: "trip_brief",
    label: "Trip brief / itinerary",
    icon: "🚗",
    placeholder:
      "e.g. Per stop: customer · last spend · open issues · what to ask. End with one risk for the day.",
    hint: "Used by trip itinerary + multi-stop debrief.",
  },
  {
    key: "chat_general",
    label: "General chat",
    icon: "💬",
    placeholder:
      "e.g. Default to short, action-first replies. Don't recap what I said. Use bullets when listing 3+ items.",
    hint: "Catch-all for the Hidro Assistant chat when no other surface matches.",
  },
];

function SurfacePrefsSection({ prefs, onSave }) {
  const [draft, setDraft] = useState(() => ({ ...(prefs || {}) }));
  const [savingKey, setSavingKey] = useState(null);
  const [savedKey, setSavedKey] = useState(null);

  // Reflect external prefs updates (initial load, wipe, etc.) into the draft.
  useEffect(() => { setDraft({ ...(prefs || {}) }); }, [prefs]);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const isDirty = (k) => (draft[k] || "") !== (prefs?.[k] || "");

  const saveOne = async (k) => {
    setSavingKey(k);
    try {
      await onSave({ [k]: (draft[k] || "").slice(0, 600) });
      setSavedKey(k);
      setTimeout(() => setSavedKey((cur) => (cur === k ? null : cur)), 2000);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <section className="hv-card p-5 space-y-4" data-testid="hidro-surface-prefs">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-[#1FB6C1]" />
        <h2 className="font-display text-lg font-bold">Teach Hidro how you want it</h2>
      </div>
      <p className="text-xs text-[#a0aabf] leading-relaxed -mt-2">
        Plain-text rules for each kind of Hidro output. Type how you want it,
        save, and Hidro applies it on the very next reply — no redeploy. Up to
        600 characters per surface.
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        {SURFACE_DEFS.map((s) => {
          const val = draft[s.key] || "";
          return (
            <div
              key={s.key}
              className="border border-white/8 rounded-md p-3 bg-black/15 space-y-2"
              data-testid={`surface-pref-${s.key}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-base shrink-0" aria-hidden>{s.icon}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-white truncate">{s.label}</div>
                    <div className="text-[10px] text-[#647087] truncate">{s.hint}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => saveOne(s.key)}
                  disabled={!isDirty(s.key) || savingKey === s.key}
                  className="hv-btn hv-btn-primary text-[11px] py-1 px-2 shrink-0"
                  data-testid={`surface-pref-save-${s.key}`}
                  title={isDirty(s.key) ? "Save this rule" : "No changes"}
                >
                  {savingKey === s.key ? (
                    <><Loader2 className="w-3 h-3 animate-spin" /> Saving</>
                  ) : savedKey === s.key ? (
                    <><Check className="w-3 h-3" /> Saved</>
                  ) : (
                    <><Sparkles className="w-3 h-3" /> Save</>
                  )}
                </button>
              </div>
              <DictTextarea
                value={val}
                onChange={(e) => setField(s.key, e.target.value)}
                placeholder={s.placeholder}
                maxLength={600}
                rows={3}
                className="hv-input text-xs w-full resize-y leading-relaxed"
                data-testid={`surface-pref-textarea-${s.key}`}
              />
              <div className="flex items-center justify-between text-[10px] text-[#647087]">
                <span>{val.length}/600</span>
                {isDirty(s.key) && <span className="text-[#1FB6C1]">unsaved</span>}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
